/**
 * Phase 13 Metrics Dashboard — useDviStageReadiness
 *
 * Per-component stage + readiness rows (manager-only).
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { RecommendationFilter, OcrBand } from './useDviLearningDashboard';

export interface StageReadinessRow {
  component: string;
  condition_type: string;
  total_samples: number;
  distinct_findings_count: number;
  multi_image_rollup_count: number;
  recent_30d_samples: number;
  component_accuracy: number;
  severity_accuracy: number;
  recommendation_accuracy: number;
  rejection_rate: number;
  edit_rate: number;
  acceptance_rate: number;
  velocity_30d: number;
  stability_score: number;
  component_stage: string;
  severity_stage: string;
  recommendation_stage: string;
  overall_stage: string;
  automation_ready: boolean;
  prefill_ready: boolean;
  job_suggestion_ready: boolean;
  readiness_blockers: string[];
  ocr_supported_component_agreement: number | null;
  ocr_unsupported_component_agreement: number | null;
  ocr_lift_component_pct: number | null;
}

export function useDviStageReadiness(
  shopId: string | null,
  recommendationFilter: RecommendationFilter = null,
  ocrBand: OcrBand = null,
) {
  return useQuery({
    queryKey: ['dvi-stage-readiness', shopId, recommendationFilter, ocrBand],
    queryFn: async (): Promise<StageReadinessRow[]> => {
      const { data, error } = await supabase.rpc('get_dvi_component_stage_readiness', {
        p_shop_id: shopId,
        p_recommendation_filter: recommendationFilter,
        p_ocr_band: ocrBand,
      });
      if (error) throw error;
      return (data ?? []) as StageReadinessRow[];
    },
    enabled: !!shopId,
    staleTime: 30 * 1000,
  });
}
